<template>
  <div class="col-12 col-xl-12">
    <!-- Filters -->
    <div class="container-fluid px-0">
      <div class="row mb-4 g-2 p-3 p-xl-4">
        <!-- Academic Year Filter -->
        <div class="col-6 col-sm-4 col-md-3 col-lg-2">
          <select
            class="form-select form-select-sm"
            v-model="localFilters.academic_year_id"
            @change="applyFilters"
          >
            <option value="">{{ $t('student_rewards.filters.all_years') }}</option>
            <option v-for="year in academicYears" :key="year.id" :value="year.id">
              {{ year.name }}
            </option>
          </select>
        </div>

        <!-- Class Filter -->
        <div class="col-6 col-sm-4 col-md-3 col-lg-2">
          <select
            class="form-select form-select-sm"
            v-model="localFilters.class_id"
            @change="handleClassChange"
          >
            <option value="">{{ $t('student_rewards.filters.all_classes') }}</option>
            <option v-for="cls in classes" :key="cls.id" :value="cls.id">
              {{ cls.name }}
            </option>
          </select>
        </div>

        <!-- Group Filter -->
        <div class="col-6 col-sm-4 col-md-3 col-lg-2">
          <select
            class="form-select form-select-sm"
            v-model="localFilters.class_group_id"
            @change="applyFilters"
          >
            <option value="">{{ $t('student_rewards.filters.all_groups') }}</option>
            <option v-for="group in groups" :key="group.id" :value="group.id">
              {{ group.name }}
            </option>
          </select>
        </div>

        <!-- Top Count Filter -->
        <div class="col-6 col-sm-4 col-md-3 col-lg-2">
          <select
            class="form-select form-select-sm"
            v-model="localFilters.top_count"
            @change="applyFilters"
          >
            <option value="10">Top 10</option>
            <option value="20">Top 20</option>
            <option value="50">Top 50</option>
            <option value="100">Top 100</option>
          </select>
        </div>

        <!-- Reset Filter (desktop) -->
        <div class="col-md-1 col-lg-1 d-none d-md-block">
          <button 
            class="btn btn-outline-secondary btn-sm w-100" 
            @click="resetFilters"
            :title="$t('filters.resetTitle')"
          >
            <i class="fas fa-filter-circle-xmark"></i>
          </button>
        </div>
   
        <!-- Reset Filter (mobile) -->
        <div class="col-md-1 col-lg-1 col-sm-1 d-md-none">
          <button class="btn btn-outline-secondary btn-sm w-100" @click="resetFilters">
            <i class="fas fa-filter-circle-xmark"></i> 
          </button>
        </div>

        <!-- Search -->
        <div class="col-12 col-sm-7 col-md-11 col-lg-2 position-relative">
          <input
            type="text"
            class="form-control form-control-sm ps-5"
            :placeholder="$t('student_rewards.filters.search_placeholder')"
            v-model="localFilters.search"
            @input="handleSearchInput"
            :dir="locale === 'ar' ? 'rtl' : 'ltr'"
            style="padding-right: 12px"
          />
          <span class="position-absolute start-0 top-50 translate-middle-y ps-3">
            <i class="fas fa-search"></i>
          </span>
        </div>
      </div>
    </div>

    <!-- Statistics Table FIRST -->
    <div class="card mb-3 mb-xxl-5 me-xxl-4" style="margin-top: 4rem; overflow: visible">
      <!-- Card Header -->
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <h6 class="text-white text-capitalize ps-3 pe-3" style="font-size: 1rem">
            {{ $t('student_rewards.statistics_chart_title') }}
          </h6>
        </div>
      </div>

      <!-- Card Body -->
      <div class="card-body px-0 pb-2 pe-xl-3">
        <!-- Statistics Table -->
        <div class="table-responsive p-1 p-xl-4">
          <LoadingSpinner
            v-if="loading"
            :message="$t('common.loading')"
            class="py-5"
          />

          <template v-else>
            <!-- Mobile View -->
            <div class="d-block d-xl-none">
              <div
                v-if="statistics.length === 0"
                class="text-center py-3 text-muted"
              >
                <i class="fas fa-chart-bar fa-2x mb-2"></i>
                <p>{{ $t('student_rewards.statistics.no_data') }}</p>
              </div>

              <div
                v-for="student in statistics"
                :key="'mobile-' + student.id"
                class="card mb-3 mx-2"
              >
                <div class="card-body py-2">
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1">
                      <span class="badge me-2" :class="getRankBadgeClass(student.rank - 1)">
                        #{{ student.rank }}
                      </span>
                      <h6 class="mb-0 text-sm d-inline">{{ student.full_name }}</h6>
                    </div>
                    <div>
                      <span
                        class="badge badge-sm"
                        :class="student.gender === 'M' ? 'bg-primary' : 'bg-secondary'"
                      >
                        {{ student.gender === 'M' ? $t('student_rewards.male') : $t('student_rewards.female') }}
                      </span>
                    </div>
                  </div>
                  
                  <div class="mt-2">
                    <p class="text-xs text-muted mb-1">
                      <i class="fas fa-graduation-cap me-1"></i>
                      {{ student.current_assignment?.class?.name || $t('student_rewards.no_class') }}
                      <span v-if="student.current_assignment?.group?.name" class="text-xs">
                        ({{ student.current_assignment.group.name }})
                      </span>
                    </p>
                  </div>
                  
                  <div class="mt-2">
                    <div class="d-flex justify-content-around">
                      <div class="text-center">
                        <span class="badge bg-success d-block mb-1">+{{ student.positive_points || 0 }}</span>
                        <small class="text-muted">{{ $t('student_rewards.positive') }}</small>
                      </div>
                      <div class="text-center">
                        <span class="badge bg-danger d-block mb-1">-{{ student.negative_points || 0 }}</span>
                        <small class="text-muted">{{ $t('student_rewards.negative') }}</small>
                      </div>
                      <div class="text-center">
                        <span class="badge d-block mb-1" :class="getTotalPointsClass(student.total_points || 0)">
                          {{ student.total_points || 0 }}
                        </span>
                        <small class="text-muted">{{ $t('student_rewards.total') }}</small>
                      </div>
                    </div>
                  </div>
                  
                  <div class="d-flex justify-content-center align-items-center mt-2">
                    <router-link
                      :to="`/students/${student.id}/reward-history`"
                      class="btn btn-link text-info p-0 mx-2"
                      :title="$t('student_rewards.view_history')"
                    >
                      <i class="fas fa-history fa-xs"></i>
                      <small class="ms-1">{{ $t('student_rewards.view_history') }}</small>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>

            <!-- Desktop View -->
            <table class="table align-items-center mb-0 d-none d-xl-table">
              <thead>
                <tr>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">
                    {{ $t('student_rewards.statistics.rank') }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">
                    {{ $t('student_rewards.statistics.student') }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">
                    {{ $t('student_rewards.statistics.class') }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">
                    {{ $t('student_rewards.gender') }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">
                    {{ $t('student_rewards.positive_points') }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">
                    {{ $t('student_rewards.negative_points') }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">
                    {{ $t('student_rewards.total_points') }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center pe-4">
                    {{ $t('common.actions') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!loading && statistics.length === 0">
                  <td colspan="8" class="text-center py-4 text-muted">
                    <i class="fas fa-chart-bar fa-2x mb-2"></i>
                    <p>{{ $t('student_rewards.statistics.no_data') }}</p>
                  </td>
                </tr>

                <template v-if="!loading">
                  <tr v-for="student in statistics" :key="student.id">
                    <td class="align-middle text-center">
                      <span class="badge" :class="getRankBadgeClass(student.rank - 1)">
                        #{{ student.rank }}
                      </span>
                    </td>
                    <td>
                      <div class="d-flex px-3 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-md">{{ student.full_name }}</h6>
                          <p class="text-xs text-secondary mb-0">
                            {{ student.national_num || student.passport_num || $t('student_rewards.no_id') }}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td class="align-middle text-center">
                      <span class="text-xs font-weight-bold">
                        {{ student.current_assignment?.class?.name || $t('student_rewards.no_class') }}
                        <span v-if="student.current_assignment?.group?.name" class="text-xs text-secondary">
                          ({{ student.current_assignment.group.name }})
                        </span>
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span
                        class="badge badge-sm"
                        :class="student.gender === 'M' ? 'bg-primary' : 'bg-secondary'"
                      >
                        {{ student.gender === 'M' ? $t('student_rewards.male') : $t('student_rewards.female') }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="badge bg-success">
                        +{{ student.positive_points || 0 }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="badge bg-danger">
                        -{{ student.negative_points || 0 }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="badge" :class="getTotalPointsClass(student.total_points || 0)">
                        {{ student.total_points || 0 }}
                      </span>
                    </td>
                    <td class="align-middle text-center pe-4">
                      <div class="d-inline-flex">
                        <router-link
                          :to="`/students/${student.id}/reward-history`"
                          class="btn btn-link text-info p-1 mx-1"
                          :title="$t('student_rewards.view_history')"
                        >
                          <i class="fas fa-history fa-sm"></i>
                        </router-link>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </template>
        </div>
      </div>
    </div>

    <!-- Chart Section SECOND -->
    <div class="card mb-3 mb-xxl-5 me-xxl-4" style="overflow: visible">
      <!-- Card Header -->
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <h6 class="text-white text-capitalize ps-3 pe-3" style="font-size: 1rem">
            {{ $t('student_rewards.top_students_chart') }}
          </h6>
        </div>
      </div>

      <!-- Card Body -->
      <div class="card-body px-0 pb-2 pe-xl-3">
        <!-- Chart Section -->
        <div class="p-3 p-xl-4">
          <div class="card mb-4">
            <div class="card-header">
              <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
                <h6 class="mb-0">{{ $t('student_rewards.top_students_chart') }}</h6>
                <div class="btn-group btn-group-sm" role="group">
                  <button
                    type="button"
                    class="btn btn-outline-primary"
                    :class="{ active: activeChart === 'positive' }"
                    @click="switchChart('positive')"
                    :title="$t('student_rewards.positive_points')"
                  >
                    <i class="fas fa-plus-circle"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-primary"
                    :class="{ active: activeChart === 'negative' }"
                    @click="switchChart('negative')"
                    :title="$t('student_rewards.negative_points')"
                  >
                    <i class="fas fa-minus-circle"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-primary"
                    :class="{ active: activeChart === 'total' }"
                    @click="switchChart('total')"
                    :title="$t('student_rewards.total_points')"
                  >
                    <i class="fas fa-chart-bar"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-primary"
                    :class="{ active: activeChart === 'comparison' }"
                    @click="switchChart('comparison')"
                    :title="$t('student_rewards.chart.comparison')"
                  >
                    <i class="fas fa-exchange-alt"></i>
                  </button>
                </div>
              </div>
            </div>
            <div class="card-body">
              <LoadingSpinner v-if="loading" class="py-5" />
              <div v-else class="chart-container" style="position: relative; width: 100%; min-height: 200px; height: 300px;">
                <canvas ref="chartCanvas"></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import { Chart, registerables } from 'chart.js'
import api from '@/api'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'

Chart.register(...registerables)

const { t, locale } = useI18n()
const toast = useToast()

// State
const loading = ref(false)
const activeChart = ref('positive')
const chartCanvas = ref(null)
let chartInstance = null

// Data
const statistics = ref([])
const academicYears = ref([])
const classes = ref([])
const groups = ref([])

let debounceTimeout = null

// Filters
const filters = ref({
  academic_year_id: '',
  class_id: '',
  class_group_id: '',
  top_count: 10,
  search: ''
})

const localFilters = ref({
  ...JSON.parse(JSON.stringify(filters.value))
})

onUnmounted(() => {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout)
  }
  if (chartInstance) {
    chartInstance.destroy()
  }
})

// Simple debounce function
const debounce = (fn, delay) => {
  return (...args) => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout)
    }
    debounceTimeout = setTimeout(() => {
      fn(...args)
      debounceTimeout = null
    }, delay)
  }
}

const handleSearchInput = debounce(() => {
  applyFilters()
}, 500)

const applyFilters = () => {
  filters.value = { ...localFilters.value }
  fetchStatistics()
}

const resetFilters = () => {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout)
    debounceTimeout = null
  }
  
  localFilters.value = {
    academic_year_id: '',
    class_id: '',
    class_group_id: '',
    top_count: 10,
    search: ''
  }
  
  filters.value = { ...localFilters.value }
  fetchStatistics()
}

// Fetch data
const fetchAcademicYears = async () => {
  try {
    const response = await api.get('/academic-years')
    academicYears.value = response.data.data
  } catch (error) {
    console.error('Error fetching academic years:', error)
    toast.error(t('errors.fetch_failed'))
  }
}

const fetchClasses = async () => {
  try {
 const response = await api.get("/classes", {
      params: {
        all: true
      }
    });    classes.value = response.data.data
  } catch (error) {
    console.error('Error fetching classes:', error)
    toast.error(t('errors.fetch_failed'))
  }
}

const fetchGroups = async () => {
  if (!localFilters.value.class_id) {
    groups.value = []
    return
  }
  
  try {
    const response = await api.get(`/classes/${localFilters.value.class_id}/groups`)
    groups.value = response.data.data
  } catch (error) {
    console.error('Error fetching groups:', error)
    toast.error(t('errors.fetch_failed'))
  }
}

const handleClassChange = () => {
  fetchGroups()
  applyFilters()
}

const fetchStatistics = async () => {
  if (loading.value) return // Prevent duplicate requests

  loading.value = true
  try {
    const params = {
      academic_year_id: filters.value.academic_year_id || null,
      class_id: filters.value.class_id || null,
      class_group_id: filters.value.class_group_id || null,
      top_count: filters.value.top_count || 10,
      search: filters.value.search || null
    }

    const response = await api.get('/student-rewards/statistics', { params })
    statistics.value = response.data.data.map((student, index) => ({
      ...student,
      rank: index + 1
    }))

    // ✅ Render default chart automatically after DOM updates
    nextTick(async () => {
      await nextTick() // ensures canvas is mounted for sure
      if (!activeChart.value) activeChart.value = 'positive'
      renderChart()
    })
  } catch (error) {
    console.error('Error fetching statistics:', error)
    toast.error(t('errors.fetch_failed'))
  } finally {
    loading.value = false
  }
}


const switchChart = (chartType) => {
  activeChart.value = chartType
  if (statistics.value.length > 0) {
    renderChart()
  }
}

const renderChart = () => {
  if (chartInstance) {
    chartInstance.destroy()
  }
  
  if (!chartCanvas.value || statistics.value.length === 0) {
    return
  }
  
  const topStudents = statistics.value.slice(0, parseInt(filters.value.top_count))
  const labels = topStudents.map(student => student.full_name)
  
  let datasets = []
  let chartTitle = ''
  
  switch (activeChart.value) {
    case 'positive':
      datasets = [{
        label: t('student_rewards.positive_points'),
        data: topStudents.map(student => student.positive_points || 0),
        backgroundColor: 'rgba(40, 167, 69, 0.7)',
        borderColor: 'rgba(40, 167, 69, 1)',
        borderWidth: 1
      }]
      chartTitle = t('student_rewards.positive_points')
      break
      
    case 'negative':
      datasets = [{
        label: t('student_rewards.negative_points'),
        data: topStudents.map(student => student.negative_points || 0),
        backgroundColor: 'rgba(220, 53, 69, 0.7)',
        borderColor: 'rgba(220, 53, 69, 1)',
        borderWidth: 1
      }]
      chartTitle = t('student_rewards.negative_points')
      break
      
    case 'total':
      datasets = [{
        label: t('student_rewards.total_points'),
        data: topStudents.map(student => student.total_points || 0),
        backgroundColor: 'rgba(78, 115, 223, 0.7)',
        borderColor: 'rgba(78, 115, 223, 1)',
        borderWidth: 1
      }]
      chartTitle = t('student_rewards.total_points')
      break
      
    case 'comparison':
      datasets = [
        {
          label: t('student_rewards.positive_points'),
          data: topStudents.map(student => student.positive_points || 0),
          backgroundColor: 'rgba(40, 167, 69, 0.7)',
          borderColor: 'rgba(40, 167, 69, 1)',
          borderWidth: 1
        },
        {
          label: t('student_rewards.negative_points'),
          data: topStudents.map(student => student.negative_points || 0),
          backgroundColor: 'rgba(220, 53, 69, 0.7)',
          borderColor: 'rgba(220, 53, 69, 1)',
          borderWidth: 1
        }
      ]
      chartTitle = t('student_rewards.chart.comparison')
      break
  }
  
  const ctx = chartCanvas.value.getContext('2d')
  
  chartInstance = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: labels,
      datasets: datasets
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: chartTitle
          }
        },
        x: {
          ticks: {
            maxRotation: 45,
            minRotation: 45,
            font: {
              size: 10
            }
          }
        }
      },
      plugins: {
        legend: {
          position: 'top',
          labels: {
            usePointStyle: true,
            font: {
              size: 11
            }
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false
        }
      }
    }
  })
}

// Helper functions
const getRankBadgeClass = (index) => {
  if (index === 0) return 'bg-warning text-dark'
  if (index === 1) return 'bg-secondary'
  if (index === 2) return 'bg-info text-dark'
  return 'bg-light text-dark'
}

const getTotalPointsClass = (points) => {
  if (points >= 100) return 'bg-success'
  if (points >= 50) return 'bg-info'
  if (points >= 0) return 'bg-primary'
  return 'bg-danger'
}

// Lifecycle
onMounted(() => {
  fetchAcademicYears()
  fetchClasses()
  fetchStatistics()
})
</script>

